import React, { useState, useEffect, useRef } from 'react'
import {
  TrendingUp, Activity, AlertCircle, ExternalLink, Play, Pause, WifiOff,
  Terminal, Trash2, RefreshCw, Wallet, Layers, Signal, ArrowRight, Sparkles,
} from 'lucide-react'
import { useErrorLog, clearErrorLog } from '../utils/errorLog'
import { portfolioAPI, signalsAPI, reportsAPI, externalHoldingsAPI, streamingAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { useAuth } from '../contexts/AuthContext'
import { useMarketAutomation } from '../contexts/MarketAutomationContext'
import { useThrottledTicks } from '../hooks/useThrottledTicks'
import { openTradingView } from '../utils/tradingView'
import { Panel, Surface, Stat, Delta, Pill, Meter } from '../components/ui'
import { inr, inrCompact, inrSigned, pct } from '../utils/format'
import { cn } from '../utils/cn'

const UnavailablePill = () => (
  <Pill tone="loss" icon={WifiOff}>Unavailable</Pill>
)

// Account composition palette — quiet, accent-led, no green (green = profit, reserved).
const ACCOUNT_META = {
  KitePM:        { label: 'Kite PM',      color: '#214AD4' },
  'Kite RJ':     { label: 'Kite RJ',      color: '#5B85FB' },
  'AngelOne RJ': { label: 'AngelOne RJ',  color: '#3A4658' },
  'AngelOne PM': { label: 'AngelOne PM',  color: '#9AA4B5' },
}

const Dashboard = () => {
  const { user } = useAuth()
  // Throttled to 5 s — prevents table row-shuffle / flicker on every WS tick
  const symbolTicks = useThrottledTicks(5000)

  // Helper: streaming LTP with fallback to API value
  const liveLTP = (symbol, apiPrice) => symbolTicks?.[symbol]?.ltp ?? apiPrice ?? 0

  const {
    isAutomationActive,
    lastWatchlistUpdate,
    lastSignalGeneration,
    automationLogs,
    startAutomation,
    stopAutomation,
  } = useMarketAutomation()

  // ── Error log ─────────────────────────────────────────────────────────────
  const errorLogs          = useErrorLog()
  const [logsAutoRefresh, setLogsAutoRefresh] = useState(true)
  const [logsTick, setLogsTick]               = useState(0)  // forces "X ago" recalc
  const logsEndRef = useRef(null)

  useEffect(() => {
    if (!logsAutoRefresh) return
    const id = setInterval(() => setLogsTick(t => t + 1), 15_000)
    return () => clearInterval(id)
  }, [logsAutoRefresh])

  const fmtAgo = (date) => {
    const s = Math.floor((Date.now() - new Date(date)) / 1000)
    if (s < 60)  return `${s}s ago`
    if (s < 3600) return `${Math.floor(s / 60)}m ago`
    return `${Math.floor(s / 3600)}h ago`
  }

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({
    positions: [],
    holdings: [],   // live Kite holdings (if connected)
    margins: null,
    signals: [],
    monthlySummary: null,
    dailyPnL: [],
  })
  // External account snapshots (fetched regardless of Kite connection)
  const [extAccounts, setExtAccounts] = useState({})
  // Per-section error flags
  const [errors, setErrors] = useState({
    positions: false,
    holdings: false,
    margins: false,
    signals: false,
    monthlySummary: false,
  })

  useEffect(() => {
    fetchDashboardData()
  }, [user])   // eslint-disable-line

  const EXT_ACCOUNTS = ['kite_pm_live', 'kite_rj', 'angelone_rj', 'angelone_pm']

  // ── Subscribe all dashboard holdings symbols to the stream ──────────────────
  useEffect(() => {
    const symSet = new Set()
    const clean = (s) => (s || '').toUpperCase().replace(/-EQ$/i, '').trim()

    data.holdings.forEach((h) => {
      const s = clean(h.tradingsymbol)
      if (s) symSet.add(s)
    })

    EXT_ACCOUNTS.forEach((acc) => {
      (extAccounts[acc]?.normalizedHoldings || []).forEach((h) => {
        const s = clean(h.symbol)
        if (s) symSet.add(s)
      })
    })

    if (symSet.size > 0) {
      streamingAPI.subscribe([...symSet]).catch(() => {})
    }
  }, [data.holdings, extAccounts])   // eslint-disable-line

  const fetchDashboardData = async () => {
    try {
      const [positionsRes, holdingsRes, marginsRes, signalsRes, summaryRes, pnlRes, ...extResults] =
        await Promise.allSettled([
          user?.kiteConnected ? portfolioAPI.getPositions()    : Promise.resolve({ data: { data: { net: [] } } }),
          user?.kiteConnected ? portfolioAPI.getHoldings()     : Promise.resolve({ data: { data: [] } }),
          user?.kiteConnected ? portfolioAPI.getMargins()      : Promise.resolve({ data: null }),
          signalsAPI.getSignals({ limit: 5 }),
          reportsAPI.getMonthlySummary(),
          portfolioAPI.getDailyPnL(),
          ...EXT_ACCOUNTS.map((a) => externalHoldingsAPI.get(a)),
        ])

      setErrors({
        positions: positionsRes.status === 'rejected',
        holdings: holdingsRes.status === 'rejected',
        margins: marginsRes.status === 'rejected',
        signals: signalsRes.status === 'rejected',
        monthlySummary: summaryRes.status === 'rejected',
      })

      setData({
        positions:
          positionsRes.status === 'fulfilled'
            ? positionsRes.value.data?.data?.net || []
            : [],
        holdings:
          holdingsRes.status === 'fulfilled'
            ? holdingsRes.value.data?.data || []
            : [],
        margins:
          marginsRes.status === 'fulfilled'
            ? marginsRes.value.data?.data || marginsRes.value.data || null
            : null,
        signals:
          signalsRes.status === 'fulfilled'
            ? signalsRes.value.data || []
            : [],
        monthlySummary:
          summaryRes.status === 'fulfilled' ? summaryRes.value.data : null,
        dailyPnL:
          pnlRes.status === 'fulfilled' && Array.isArray(pnlRes.value.data)
            ? pnlRes.value.data.slice(0, 7)
            : [],
      })

      const ext = {}
      EXT_ACCOUNTS.forEach((acc, i) => {
        if (extResults[i]?.status === 'fulfilled') {
          ext[acc] = extResults[i].value.data
        }
      })
      setExtAccounts(ext)
    } catch (error) {
      console.error('Dashboard data fetch error:', error)
    } finally {
      setLoading(false)
    }
  }

  const getHoldingQuantity = (h) => (h.quantity || 0) + (h.t1_quantity || 0)

  const getAvailableFunds = (margins) => {
    if (!margins) return null
    return (
      margins?.equity?.available?.live_balance ??
      margins?.equity?.available?.cash ??
      margins?.equity?.net ??
      null
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  // ── Kite live holdings (streaming LTP override) ───────────────────────────
  const kiteHoldingsValue = data.holdings.reduce(
    (s, h) => s + liveLTP(h.tradingsymbol, h.last_price) * getHoldingQuantity(h),
    0
  )
  const kiteHoldingsPnL = data.holdings.reduce((s, h) => {
    const ltp = liveLTP(h.tradingsymbol, h.last_price)
    const qty = getHoldingQuantity(h)
    const cost = (h.average_price || 0) * qty
    return s + (cost > 0 ? ltp * qty - cost : h.pnl || 0)
  }, 0)

  // ── External accounts cumulative — recompute from live LTPs ─────────────────
  const computeExtValue = (acc) => {
    const holdings = extAccounts[acc]?.normalizedHoldings || []
    return holdings.reduce((sum, h) => {
      const qty = (h.quantity || 0) + (h.t1Quantity || 0)
      const ltp = symbolTicks?.[h.symbol]?.ltp ?? h.ltp ?? 0
      return sum + (ltp * qty)
    }, 0)
  }

  const computeExtPnl = (acc) => {
    const holdings = extAccounts[acc]?.normalizedHoldings || []
    return holdings.reduce((sum, h) => {
      const qty = (h.quantity || 0) + (h.t1Quantity || 0)
      const ltp = symbolTicks?.[h.symbol]?.ltp ?? h.ltp ?? 0
      const cost = (h.averagePrice || 0) * qty
      return sum + (cost > 0 ? ltp * qty - cost : h.pnl || 0)
    }, 0)
  }

  const kiteIsLive  = data.holdings.length > 0
  const kitePMValue = kiteIsLive ? kiteHoldingsValue : computeExtValue('kite_pm_live')
  const kitePMPnL   = kiteIsLive ? kiteHoldingsPnL   : computeExtPnl('kite_pm_live')

  const totalPortfolioValue = kitePMValue + computeExtValue('kite_rj') + computeExtValue('angelone_rj') + computeExtValue('angelone_pm')
  const totalPortfolioPnL   = kitePMPnL   + computeExtPnl('kite_rj')   + computeExtPnl('angelone_rj')   + computeExtPnl('angelone_pm')

  const totalPositionsPnL = data.positions.reduce((s, p) => {
    const ltp = liveLTP(p.tradingsymbol, p.last_price)
    return s + (p.quantity !== 0
      ? ltp * (p.quantity || 0) - (p.average_price || 0) * (p.quantity || 0)
      : p.pnl || 0)
  }, 0)

  const availableFunds = getAvailableFunds(data.margins)
  const usedMargin =
    data.margins?.equity?.used?.debits ?? data.margins?.equity?.utilised?.debits ?? 0

  const allHoldingsForDashboard = [
    ...data.holdings.map((h) => ({
      tradingsymbol: h.tradingsymbol,
      quantity: getHoldingQuantity(h),
      ltp: liveLTP(h.tradingsymbol, h.last_price),
      currentValue: liveLTP(h.tradingsymbol, h.last_price) * getHoldingQuantity(h),
      pnl: kiteHoldingsPnL / Math.max(data.holdings.length, 1),
      average_price: h.average_price || 0,
      _account: 'KitePM',
      _raw: h,
    })),
    ...(!kiteIsLive ? (extAccounts.kite_pm_live?.normalizedHoldings || []).map((h) => ({
      tradingsymbol: h.symbol,
      quantity: h.quantity,
      ltp: symbolTicks?.[h.symbol]?.ltp || h.ltp || 0,
      currentValue: (symbolTicks?.[h.symbol]?.ltp || h.ltp || 0) * h.quantity,
      pnl: h.pnl || 0,
      average_price: h.averagePrice || 0,
      _account: 'KitePM (cached)',
    })) : []),
    ...(extAccounts.kite_rj?.normalizedHoldings || []).map((h) => ({
      tradingsymbol: h.symbol,
      quantity: h.quantity,
      ltp: symbolTicks?.[h.symbol]?.ltp || h.ltp || 0,
      currentValue: (symbolTicks?.[h.symbol]?.ltp || h.ltp || 0) * h.quantity,
      pnl: h.pnl || 0,
      average_price: h.averagePrice || 0,
      _account: 'Kite RJ',
    })),
    ...(extAccounts.angelone_rj?.normalizedHoldings || []).map((h) => ({
      tradingsymbol: h.symbol,
      quantity: h.quantity,
      ltp: symbolTicks?.[h.symbol]?.ltp || h.ltp || 0,
      currentValue: (symbolTicks?.[h.symbol]?.ltp || h.ltp || 0) * h.quantity,
      pnl: h.pnl || 0,
      average_price: h.averagePrice || 0,
      _account: 'AngelOne RJ',
    })),
    ...(extAccounts.angelone_pm?.normalizedHoldings || []).map((h) => ({
      tradingsymbol: h.symbol,
      quantity: h.quantity,
      ltp: symbolTicks?.[h.symbol]?.ltp || h.ltp || 0,
      currentValue: (symbolTicks?.[h.symbol]?.ltp || h.ltp || 0) * h.quantity,
      pnl: h.pnl || 0,
      average_price: h.averagePrice || 0,
      _account: 'AngelOne PM',
    })),
  ].sort((a, b) => (b.currentValue || 0) - (a.currentValue || 0))

  const activeSignalCount = Array.isArray(data.signals)
    ? data.signals.filter((s) => s.status === 'ACTIVE').length
    : 0
  const activePositionCount = Array.isArray(data.positions)
    ? data.positions.filter((p) => (p.quantity || 0) !== 0).length
    : 0

  // ── Derived view-model ────────────────────────────────────────────────────
  const investedCapital = totalPortfolioValue - totalPortfolioPnL
  const totalPnlPct = investedCapital > 0 ? (totalPortfolioPnL / investedCapital) * 100 : null
  const aggregatePnl = totalPortfolioPnL + totalPositionsPnL

  const accounts = [
    { key: 'KitePM',       value: kitePMValue,                    pnl: kitePMPnL },
    { key: 'Kite RJ',      value: computeExtValue('kite_rj'),     pnl: computeExtPnl('kite_rj') },
    { key: 'AngelOne RJ',  value: computeExtValue('angelone_rj'), pnl: computeExtPnl('angelone_rj') },
    { key: 'AngelOne PM',  value: computeExtValue('angelone_pm'), pnl: computeExtPnl('angelone_pm') },
  ].filter(a => a.value > 0)
  const topValue = allHoldingsForDashboard[0]?.currentValue || 1

  return (
    <div className="space-y-6">
      {/* ════ HERO — net worth + P&L + account composition ════ */}
      <Surface tone="raised" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-[0.5] pointer-events-none" />
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-accent-500/10 blur-3xl pointer-events-none" />

        <div className="relative p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            {/* Net worth */}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="kicker">Net worth · all accounts</span>
                <Pill tone="gain" dot>Live</Pill>
              </div>
              <div className="mt-2 flex items-end gap-4 flex-wrap">
                <span className="stat-value text-[clamp(2.75rem,6vw,4.25rem)] leading-[0.95]">
                  {inrCompact(totalPortfolioValue)}
                </span>
                <Delta value={totalPortfolioPnL} pct={totalPnlPct} compact size="lg" className="mb-2" />
              </div>
              <p className="mt-2 text-sm text-ink-500">
                {inr(investedCapital)} invested · {allHoldingsForDashboard.length} holdings · {activePositionCount} open positions
              </p>

              {/* Account composition bar */}
              {accounts.length > 0 && (
                <div className="mt-6 max-w-xl">
                  <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-ink-100">
                    {accounts.map((a) => (
                      <div
                        key={a.key}
                        title={`${ACCOUNT_META[a.key]?.label || a.key} · ${inrCompact(a.value)}`}
                        style={{
                          width: `${(a.value / totalPortfolioValue) * 100}%`,
                          backgroundColor: ACCOUNT_META[a.key]?.color || '#9AA4B5',
                        }}
                        className="first:rounded-l-full last:rounded-r-full transition-all duration-700 ease-out-quint"
                      />
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
                    {accounts.map((a) => (
                      <div key={a.key} className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ACCOUNT_META[a.key]?.color }} />
                        <span className="text-xs font-medium text-ink-600">{ACCOUNT_META[a.key]?.label || a.key}</span>
                        <span className="text-xs text-ink-400 tabular-nums">{inrCompact(a.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Aggregate P&L callout */}
            <div className="shrink-0 lg:text-right">
              <Stat
                align="right"
                kicker="Total P&L · incl. positions"
                value={<span className={aggregatePnl >= 0 ? 'text-gain-600' : 'text-loss-600'}>{inrSigned(aggregatePnl, { compact: true })}</span>}
                foot={user?.kiteConnected && availableFunds != null ? `${inrCompact(availableFunds)} buying power` : undefined}
                size="lg"
              />
            </div>
          </div>
        </div>
      </Surface>

      {/* ════ METRIC TILES ════ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Panel className="!p-5">
          <Stat
            kicker="Invested capital" icon={Wallet}
            value={inrCompact(investedCapital)}
            foot="cost basis · all accounts"
          />
        </Panel>

        <Panel className="!p-5">
          {!user?.kiteConnected || availableFunds === null ? (
            <div>
              <div className="flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5 text-ink-400" /><span className="kicker">Available funds</span></div>
              <p className="mt-3 text-sm text-ink-400">Connect Kite for live balance</p>
            </div>
          ) : (
            <Stat
              kicker="Available funds" icon={Wallet}
              value={inrCompact(availableFunds)}
              foot={usedMargin > 0 ? `${inrCompact(usedMargin)} used` : 'Kite PM'}
            />
          )}
        </Panel>

        <Panel className="!p-5">
          {errors.positions ? (
            <div><span className="kicker">Active positions</span><div className="mt-3"><UnavailablePill /></div></div>
          ) : (
            <Stat
              kicker="Active positions" icon={Layers}
              value={activePositionCount}
              delta={totalPositionsPnL !== 0 ? { value: totalPositionsPnL, compact: true } : undefined}
              foot={totalPositionsPnL !== 0 ? "today" : `${allHoldingsForDashboard.length} holdings`}
            />
          )}
        </Panel>

        <Panel className="!p-5">
          {errors.signals ? (
            <div><span className="kicker">Active signals</span><div className="mt-3"><UnavailablePill /></div></div>
          ) : (
            <Stat
              kicker="Active signals" icon={Signal}
              value={activeSignalCount}
              foot={`${Array.isArray(data.signals) ? data.signals.length : 0} total`}
            />
          )}
        </Panel>
      </div>

      {/* ════ HOLDINGS + POSITIONS ════ */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Top Holdings — weight-ranked */}
        <Panel
          className="lg:col-span-3"
          kicker={`Top holdings · ${allHoldingsForDashboard.length}`}
          title="Largest positions by value"
          action={<a href="/portfolio" className="text-sm font-medium text-accent-600 hover:text-accent-700 inline-flex items-center gap-1">All <ArrowRight className="w-3.5 h-3.5" /></a>}
        >
          {allHoldingsForDashboard.length === 0 ? (
            <EmptyState icon={Layers} title="No holdings yet" sub="Connect Kite or upload holdings in Portfolio" />
          ) : (
            <div className="space-y-1.5">
              {allHoldingsForDashboard.slice(0, 7).map((h, i) => {
                const weight = (h.currentValue || 0) / topValue
                const isLive = !!symbolTicks?.[h.tradingsymbol]
                return (
                  <div key={i} className="group relative flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-ink-50 transition-colors">
                    {/* weight bar backdrop */}
                    <div className="absolute inset-y-1.5 left-0 rounded-lg bg-accent-500/[0.06]" style={{ width: `${Math.max(8, weight * 100)}%` }} />
                    <div className="relative flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-7 text-center text-xs font-semibold text-ink-300 tabular-nums">{i + 1}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-ink-900 truncate">{h.tradingsymbol}</span>
                          {isLive && <span className="w-1.5 h-1.5 rounded-full bg-gain-500 animate-sheen" title="Live price" />}
                          <button onClick={() => openTradingView(h.tradingsymbol)} className="text-ink-300 hover:text-accent-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                          <span className="text-[10px] font-medium text-ink-400 bg-ink-100 px-1.5 py-0.5 rounded-md">{h._account}</span>
                        </div>
                        <div className="text-xs text-ink-400 tabular-nums mt-0.5">{h.quantity} @ {inr(h.average_price, { decimals: 2 })}</div>
                      </div>
                    </div>
                    <div className="relative text-right shrink-0">
                      <div className="font-semibold text-ink-900 tabular-nums">{inrCompact(h.currentValue)}</div>
                      <Delta value={h.pnl} size="xs" arrow={false} className="justify-end" />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Panel>

        {/* Today's Positions */}
        <Panel
          className="lg:col-span-2"
          kicker="Today"
          title="Open positions"
          action={<a href="/portfolio" className="text-sm font-medium text-accent-600 hover:text-accent-700">View</a>}
        >
          {errors.positions ? (
            <EmptyState icon={WifiOff} title="Position data unavailable" />
          ) : data.positions.length === 0 ? (
            <EmptyState icon={TrendingUp} title="No open positions" sub="Intraday & short-term positions appear here" />
          ) : (
            <div className="space-y-1.5">
              {data.positions.slice(0, 6).map((p, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl px-3 py-2.5 hover:bg-ink-50 transition-colors">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-ink-900 truncate">{p.tradingsymbol}</span>
                      <button onClick={() => openTradingView(p.tradingsymbol, p.exchange)} className="text-ink-300 hover:text-accent-600"><ExternalLink className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="text-xs text-ink-400 tabular-nums mt-0.5">{p.quantity} @ {inr(p.average_price, { decimals: 2 })}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm text-ink-600 tabular-nums">{inr(liveLTP(p.tradingsymbol, p.last_price), { decimals: 2 })}</div>
                    <Delta value={p.pnl} size="xs" arrow={false} className="justify-end" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* ════ SIGNALS + MONTHLY ════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {!errors.signals && Array.isArray(data.signals) && data.signals.length > 0 && (
          <Panel kicker="Latest" title="Recent signals"
            action={<a href="/signals" className="text-sm font-medium text-accent-600 hover:text-accent-700">All</a>}>
            <div className="space-y-1.5">
              {data.signals.map((signal) => (
                <div key={signal._id} className="flex items-center justify-between rounded-xl px-3 py-2.5 hover:bg-ink-50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={cn('w-1.5 h-8 rounded-full shrink-0', signal.signalType === 'BUY' ? 'bg-gain-500' : 'bg-loss-500')} />
                    <div className="min-w-0">
                      <p className="font-semibold text-ink-900 truncate">{signal.symbol}</p>
                      <p className="text-xs text-ink-400 truncate">{(signal.strategy || '').replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <Pill tone={signal.signalType === 'BUY' ? 'gain' : 'loss'}>{signal.signalType}</Pill>
                    <p className="text-xs text-ink-400 mt-1 tabular-nums">{signal.confidence}% conf.</p>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        )}

        {!errors.monthlySummary && data.monthlySummary && (
          <Panel kicker="This month" title="Performance">
            <div className="grid grid-cols-2 gap-x-6 gap-y-5">
              <MiniStat label="Total trades" value={data.monthlySummary.totalTrades ?? '—'} />
              <MiniStat label="Realized P&L"
                value={<span className={(data.monthlySummary.totalPnL || 0) >= 0 ? 'text-gain-600' : 'text-loss-600'}>{inrSigned(data.monthlySummary.totalPnL || 0, { compact: true })}</span>} />
              <MiniStat label="Signals generated" value={data.monthlySummary.totalSignals ?? '—'} />
              <div>
                <div className="text-xs font-medium text-ink-500 mb-1.5">Win rate · {(data.monthlySummary.winRate || 0).toFixed(1)}%</div>
                <Meter value={(data.monthlySummary.winRate || 0) / 100} tone={(data.monthlySummary.winRate || 0) >= 50 ? 'gain' : 'amber'} size="sm" />
              </div>
            </div>
          </Panel>
        )}
      </div>

      {/* ════ AUTOMATION ════ */}
      {user?.kiteConnected && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel
            kicker="Engine" title="Market automation"
            action={
              <button onClick={isAutomationActive ? stopAutomation : startAutomation}
                className={isAutomationActive ? 'btn-danger !py-1.5 !px-3 text-sm' : 'btn-primary !py-1.5 !px-3 text-sm'}>
                {isAutomationActive ? <><Pause className="w-4 h-4" />Stop</> : <><Play className="w-4 h-4" />Start</>}
              </button>
            }
          >
            <div className="space-y-3">
              <Row label="Status" value={<Pill tone={isAutomationActive ? 'gain' : 'warning'} dot>{isAutomationActive ? 'Active' : 'Idle'}</Pill>} />
              {lastWatchlistUpdate && <Row label="Last watchlist update" value={<span className="text-sm font-medium text-ink-700 tabular-nums">{lastWatchlistUpdate.toLocaleString()}</span>} />}
              {lastSignalGeneration && <Row label="Last signal generation" value={<span className="text-sm font-medium text-ink-700 tabular-nums">{lastSignalGeneration.toLocaleString()}</span>} />}
            </div>
          </Panel>

          <Panel kicker="Stream" title="Automation activity">
            <div className="space-y-1 max-h-64 overflow-y-auto -mr-2 pr-2">
              {(automationLogs || []).slice(0, 12).map((log) => (
                <div key={log.id} className="flex items-start gap-3 rounded-lg px-2 py-1.5 hover:bg-ink-50 transition-colors">
                  <span className={cn('w-1.5 h-1.5 rounded-full mt-1.5 shrink-0',
                    log.type === 'success' ? 'bg-gain-500' : log.type === 'warning' ? 'bg-amber-500' : log.type === 'error' ? 'bg-loss-500' : 'bg-accent-500')} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-ink-700">{log.message}</p>
                    <p className="text-xs text-ink-400 tabular-nums">{log.timestamp?.toLocaleTimeString?.() || ''}</p>
                  </div>
                </div>
              ))}
              {(!automationLogs || automationLogs.length === 0) && <EmptyState icon={Sparkles} title="No activity yet" compact />}
            </div>
          </Panel>
        </div>
      )}

      {/* ════ LOGS ════ */}
      <Panel
        header={
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Terminal className="w-4 h-4 text-ink-400" />
              <h3 className="text-base font-semibold text-ink-900">System log</h3>
              {errorLogs.length > 0 && <Pill tone="loss">{errorLogs.length}</Pill>}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setLogsAutoRefresh(v => !v)}
                title={logsAutoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh'}
                className={cn('inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg border transition-colors',
                  logsAutoRefresh ? 'border-accent-200 bg-accent-50 text-accent-700' : 'border-ink-200 bg-ink-50 text-ink-500')}>
                <RefreshCw className={cn('w-3 h-3', logsAutoRefresh && 'animate-spin-slow')} />
                {logsAutoRefresh ? 'Auto 15s' : 'Paused'}
              </button>
              {errorLogs.length > 0 && (
                <button onClick={clearErrorLog} className="inline-flex items-center gap-1 text-xs text-ink-400 hover:text-loss-600 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />Clear
                </button>
              )}
            </div>
          </div>
        }
      >
        {errorLogs.length === 0 ? (
          <EmptyState icon={Terminal} title="All systems nominal" sub="No errors logged" compact />
        ) : (
          <div className="space-y-1 max-h-72 overflow-y-auto -mr-2 pr-2" style={{ scrollbarGutter: 'stable' }}>
            {errorLogs.map((entry) => (
              <div key={entry.id} className="flex items-start gap-3 px-3 py-2 rounded-xl bg-ink-50 hover:bg-ink-100 transition-colors">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-loss-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-ink-700 break-words font-mono">{entry.message}</p>
                  {entry.source && <p className="text-xs text-ink-400 mt-0.5">{entry.source}</p>}
                </div>
                <span className="text-xs text-ink-400 shrink-0 tabular-nums" data-tick={logsTick}>{fmtAgo(entry.timestamp)}</span>
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        )}
      </Panel>
    </div>
  )
}

// ── Local presentational helpers ─────────────────────────────────────────────
function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-xs font-medium text-ink-500 mb-1">{label}</div>
      <div className="stat-value text-2xl">{value}</div>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-ink-500">{label}</span>
      {value}
    </div>
  )
}

function EmptyState({ icon: Icon, title, sub, compact }) {
  return (
    <div className={cn('text-center', compact ? 'py-6' : 'py-10')}>
      <div className="mx-auto w-11 h-11 rounded-2xl bg-ink-100 flex items-center justify-center mb-3">
        <Icon className="w-5 h-5 text-ink-400" />
      </div>
      <p className="text-sm font-medium text-ink-700">{title}</p>
      {sub && <p className="text-xs text-ink-400 mt-1">{sub}</p>}
    </div>
  )
}

export default Dashboard
